<template>
  <section
    v-if="!Array.isArray(data.acf_fields)"
    :class="[$style.articleBannerWrapper, { [$style.articleBannerWrapper_clubEvents]: isClubEvents }]"
  >
    <div
      :class="[
        $style.articleBanner,
        $style[`articleBanner_${isClubEvents ? 'right' : data.acf_fields?.left_or_right ? 'left' : 'right'}`],
      ]"
      hreflang="en"
    >
      <div :class="$style.articleBanner__picture">
        <NuxtPicture
          :src="bannerPost.image?.img"
          format="webp"
          quality="90"
          :img-attrs="{
            alt: bannerPost.image?.img_alt || bannerPost.image?.alt || bannerPost.image?.description,
            loading: 'lazy',
          }"
        />
      </div>

      <div :class="$style.articleBanner__gradient"></div>

      <div :class="$style.articleBanner__content">
        <UiBaseTags
          v-if="!isClubEvents"
          :flags="bannerPost.flags"
          :size="'small'"
          :text-color="'white'"
          :center-pos="true"
        />
        <div :class="$style.articleBanner__text">
          <h2 @click="openArticle" :class="$style.articleBanner__title">
            <span :class="$style.articleBanner__title_first">{{ data?.acf_fields?.title_1 }} </span>
            <span :class="$style.articleBanner__title_second">{{ data?.acf_fields?.title_2 }} </span>
          </h2>
          <div v-if="isClubEvents && (location || eventType)" :class="$style.articleBanner__meta">
            <EventLocation v-if="location" :location="location" />
            <span v-if="eventType">{{ eventType }}</span>
          </div>
          <p @click="openArticle" :class="$style.articleBanner__description">
            {{ data?.acf_fields?.description }}
          </p>
          <p v-if="!isClubEvents" :class="$style.articleBanner__author">
            by {{ bannerPost.author?.name }}
            {{ bannerPost.author?.last_name }}
          </p>
          <p v-else-if="contentsLabel" :class="$style.articleBanner__contents">What is inside: {{ contentsLabel }}</p>
        </div>
        <UiBaseButton
          v-if="isClubEvents"
          :class="$style.articleBanner__action"
          :text="actionText"
          variant="opacity"
          contentType="text"
          type="button"
          @click="openArticle"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import ClubEventPopup from "~/components/modals/calendarEvent/ClubEventPopup.vue";
import EventLocation from "~/components/ui/EventLocation.vue";
import type { ClubEventApiInner } from "~/types/club-events";
import { getClubEventLocation, getClubEventReportSlug, normalizeClubEvent } from "~/utils/club-events";

type ClubBannerPost = TBanner["post"] & Partial<ClubPost>;

const props = defineProps<{
  data: {
    acf_fields: TBanner;
  };
  variant?: "default" | "club-events";
}>();

const isClubEvents = computed(() => props.variant === "club-events");
const bannerPost = computed(() => props.data.acf_fields.post as ClubBannerPost);
const eventInner = computed(
  () => bannerPost.value.eventModalACF?.e_content_column__inner as ClubEventApiInner | undefined,
);
const eventStatus = computed(() => (eventInner.value?.event_is_upcoming === false ? "past" : "upcoming"));
const location = computed(() => {
  if (!isClubEvents.value || !bannerPost.value.eventModalACF) return "";
  return getClubEventLocation(bannerPost.value as ClubPost);
});
const eventType = computed(() => bannerPost.value.cats?.[0]?.name || bannerPost.value.cat?.[0]?.name || "");
const contentsLabel = computed(() => {
  if (eventStatus.value !== "past") return "";

  const contents = ["photo report"];
  if (eventInner.value?.event_post_report_vimeo) contents.push("video recap");
  if (eventInner.value?.event_post_report) contents.push("key takeaways");

  return contents.join(", ");
});
const actionText = computed(() => (eventStatus.value === "past" ? "VIEW EVENT RECAP" : "LEARN MORE & REGISTER"));

const showArticle = (slug: string, modalManager: IModalManager): void => {
  const BasePost = defineAsyncComponent(() => import("~/components/post/BasePost.vue"));
  modalManager.showModal(markRaw(BasePost) as unknown as ModalComponent, {
    slug,
    centerPos: false,
    isPost: true,
    url: `/articles/${slug}`,
    isShowOptionBtn: true,
    isShowContactForm: true,
  });
};

const openArticle = async () => {
  const nuxtApp = useNuxtApp();
  const modalManager = nuxtApp.$modalManager as IModalManager;

  modalManager.hideModal();

  if (isClubEvents.value && bannerPost.value.eventModalACF) {
    const reportSlug = getClubEventReportSlug(bannerPost.value as ClubPost);

    if (eventStatus.value === "past" && reportSlug) {
      showArticle(reportSlug, modalManager);
      return;
    }

    modalManager.showModal(markRaw(ClubEventPopup) as object as ModalComponent, {
      hideBody: true,
      shouldRedirectOnClose: false,
      centerPosY: true,
      hideHeaderClose: true,
      props: {
        event: normalizeClubEvent(bannerPost.value as ClubPost, eventStatus.value),
      },
    });
    return;
  }

  const slug = bannerPost.value.slug;
  showArticle(slug, modalManager);
};
</script>

<style lang="scss" module>
.articleBannerWrapper {
  margin: 40px;
  transition: 0.15s;

  .articleBanner {
    border-radius: 0.625rem;

    position: relative;
    display: flex;
    align-items: center;
    height: 34.375rem;
    overflow: hidden;

    &__picture {
      transition: 0.3s;

      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 0;
      border-radius: 0.625rem;
      overflow: hidden;

      img {
        transition: 0.3s;

        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &__gradient {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
      border-radius: 0.625rem;
      overflow: hidden;
    }

    &__content {
      color: v.$color-white;
      box-sizing: content-box;
      position: relative;
      z-index: 2;
      width: 27.5rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1.5625rem;

      @include v.to(tablet) {
        margin: 0 auto;
      }
    }

    &_right {
      justify-content: flex-end;

      .articleBanner__content {
        padding-right: 5rem;

        @include v.to(tablet) {
          padding-right: 0;
        }
      }

      .articleBanner__gradient {
        background: linear-gradient(90deg, rgba(v.$color-black, 0) 40%, rgba(v.$color-black, 0.8) 100%);

        @include v.to(tablet) {
          background: linear-gradient(180deg, rgba(v.$color-black, 0) 0%, rgba(v.$color-black, 0.9) 100%);
        }
      }
    }

    &_left {
      justify-content: flex-start;

      .articleBanner__content {
        padding-left: 5rem;

        @include v.to(tablet) {
          padding-left: 0;
        }
      }

      .articleBanner__gradient {
        background: linear-gradient(90deg, rgba(v.$color-black, 0.8) 0%, rgba(v.$color-black, 0) 60%);

        @include v.to(tablet) {
          background: linear-gradient(180deg, rgba(v.$color-black, 0) 0%, rgba(v.$color-black, 0.9) 100%);
        }
      }
    }

    &__text {
      display: flex;
      flex-direction: column;
      text-align: center;
      gap: 0.75rem;
    }

    &__title {
      &:hover {
        cursor: pointer;
      }
      &_first {
        @include t.headline-light-h3;

        & {
          display: block;
        }
      }

      &_second {
        @include t.headline-h3;
      }
    }

    &__description {
      &:hover {
        cursor: pointer;
      }
      @include t.text-default;

      @include v.to(tablet) {
        padding-bottom: 0.625rem;
      }
    }

    &__author {
      @include t.text-category-author;
    }
  }

  @include v.to(tablet) {
    margin: var(--gutter-mobile) auto 0;
    margin-top: 80px;

    .articleBanner {
      height: 37.5rem;
      padding: 1.875rem;
      align-items: flex-end;

      &__content {
        padding: 0;
      }
    }
  }

  &_clubEvents {
    .articleBanner {
      &__content {
        gap: 18px;
      }

      &__text {
        align-items: center;
        gap: 14px;
      }

      &__meta {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        font-family: "Futura PT", sans-serif;
        font-size: 8px;
        line-height: 12px;
        letter-spacing: 1.2px;
        text-transform: uppercase;

        p {
          margin: 0;
        }

        :global(svg path) {
          fill: v.$color-white;
        }
      }

      &__description {
        max-width: 410px;
      }

      &__contents {
        font-family: "Futura PT", sans-serif;
        font-size: 8px;
        line-height: 12px;
        letter-spacing: 1.2px;
        text-transform: uppercase;
      }

      &__action {
        min-width: 124px;
      }
    }
  }
}
</style>
